<script setup>
// Webchat de continuidade por inbox WhatsApp, bloco da aba Configurações: SÓ o
// liga/desliga (`inboxes.webchat_config.enabled`), salvo no toque, fora do
// botão Atualizar da aba. Convite, mensagens pagas no WhatsApp,
// chave do classificador e identidade valem pra conta inteira e ficam em
// Configurações → Webchat (.ai/webchat-aba-redesenho-plan.md §6). Com o toggle
// ligado, a IA NÃO roda no WhatsApp: a inbox responde ao cliente só com o
// link (1 mensagem paga) e o atendimento segue na tela.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AccountAPI from 'dashboard/api/account';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { toast } from 'dashboard/helper/toast';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  inbox: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const store = useStore();
const { accountScopedRoute } = useAccount();

const enabled = ref(false);
watch(
  () => props.inbox.webchat_config,
  cfg => {
    enabled.value = cfg?.enabled === true;
  },
  { immediate: true }
);

const isSaving = computed(() => store.getters['inboxes/getUIFlags'].isUpdating);

// Quem convida/atende pela IA NESTA caixa — vem do backend pela mesma regra
// do porteiro (Webchat::Attendant). Antes a tela olhava só `ai_project_id` e
// dizia "sem IA" pra caixa atendida pelo N8N.
const attendant = ref(null);
const attendantError = ref('');
const attendantLoading = ref(true);
onMounted(async () => {
  try {
    const { data } = await AccountAPI.webchatAttendants();
    attendant.value =
      (data.attendants || []).find(a => a.inbox_id === props.inbox.id) || null;
  } catch (error) {
    attendantError.value = error?.message || String(error);
  } finally {
    attendantLoading.value = false;
  }
});
const attendantText = computed(() => {
  if (attendantLoading.value)
    return t('INBOX_MGMT.WEBCHAT_CONFIG.ATTENDANT_LOADING');
  if (attendantError.value)
    return t('INBOX_MGMT.WEBCHAT_CONFIG.ATTENDANT_ERROR', {
      error: attendantError.value,
    });
  const row = attendant.value;
  if (!row) return t('INBOX_MGMT.WEBCHAT_CONFIG.ATTENDANT_NONE');
  const key = `INBOX_MGMT.WEBCHAT_CONFIG.ATTENDANT_${row.mode.toUpperCase()}`;
  if (row.mode === 'studio') return t(key, { flow: row.flow_name });
  if (row.mode === 'external')
    return t(key, { rules: (row.rules || []).map(r => r.name).join(', ') });
  return t(key);
});
const attendantWarns = computed(
  () =>
    !attendantLoading.value &&
    (attendantError.value ||
      !attendant.value ||
      attendant.value.mode === 'none')
);

// Salva no toque: é um switch só. Recusa do backend (feature desligada na
// conta) volta o switch e sai no toast.
const toggle = async value => {
  const previous = enabled.value;
  enabled.value = value;
  const request = store.dispatch('inboxes/updateInbox', {
    id: props.inbox.id,
    formData: false,
    webchat_config: { enabled: value },
  });
  toast.promise(request, {
    loading: t('INBOX_MGMT.WEBCHAT_CONFIG.SAVING'),
    success: t('INBOX_MGMT.WEBCHAT_CONFIG.SAVED'),
    error: error =>
      t('INBOX_MGMT.WEBCHAT_CONFIG.ERROR_SAVE', {
        error: error?.message || String(error),
      }),
  });
  try {
    await request;
  } catch {
    enabled.value = previous;
  }
};
</script>

<template>
  <SettingsSection
    :title="t('INBOX_MGMT.WEBCHAT_CONFIG.MAIN_TITLE')"
    :sub-title="t('INBOX_MGMT.WEBCHAT_CONFIG.MAIN_DESCRIPTION')"
    :show-border="false"
  >
    <div class="flex flex-col gap-3">
      <label
        class="flex items-center gap-3 p-4 mb-0 rounded-xl border border-solid border-n-weak bg-n-solid-2 cursor-pointer"
      >
        <Switch
          :model-value="enabled"
          :disabled="isSaving"
          :aria-label="t('INBOX_MGMT.WEBCHAT_CONFIG.ENABLED')"
          @update:model-value="toggle"
        />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.WEBCHAT_CONFIG.ENABLED') }}
        </span>
      </label>
      <router-link
        :to="accountScopedRoute('webchat_settings_index')"
        class="flex items-center gap-2 text-sm text-n-blue-11 hover:underline w-fit"
      >
        <span class="i-lucide-settings-2 size-4 flex-shrink-0" />
        {{ t('INBOX_MGMT.WEBCHAT_CONFIG.ACCOUNT_LINK') }}
      </router-link>
      <div
        data-test="attendant"
        class="flex items-start gap-2 px-3 py-2 text-sm rounded-xl border border-solid"
        :class="
          attendantWarns
            ? 'border-n-amber-4 bg-n-amber-3 text-n-amber-11'
            : 'border-n-weak bg-n-solid-2 text-n-slate-11'
        "
      >
        <span
          class="size-4 flex-shrink-0 mt-0.5"
          :class="attendantWarns ? 'i-lucide-triangle-alert' : 'i-lucide-bot'"
        />
        <span>
          <span class="font-medium">
            {{ t('INBOX_MGMT.WEBCHAT_CONFIG.ATTENDANT_LABEL') }}
          </span>
          {{ attendantText }}
        </span>
      </div>
    </div>
  </SettingsSection>
</template>
